import { INFINITE_DEGREES } from './consts';
import type { DependencyMapViewSettings } from './types';

/**
 * "Unlimited" reaches the view in three shapes, because `Infinity` is not
 * JSON-serializable:
 *   - `Infinity` in memory,
 *   - `null` once a slide has round-tripped through the API,
 *   - `-1`, the sentinel the degree sliders write.
 * Resolves all of them to `Infinity`. Idempotent.
 */
export const safePositiveNumeric = (
  value: number | null | undefined
): number => {
  if (value === null || value === undefined) {
    return Infinity;
  }
  return value < 0 ? Infinity : value;
};

/**
 * Read dependency map view settings into their canonical in-memory form. Every
 * environment reads its settings through this at the boundary, so nothing
 * downstream has to know about the storage conventions.
 */
export const readDependencyMapViewSettings = (
  viewSettings: DependencyMapViewSettings
): DependencyMapViewSettings => ({
  ...viewSettings,
  treeDepth: safePositiveNumeric(viewSettings.treeDepth),
  degreesOfParenthood: safePositiveNumeric(viewSettings.degreesOfParenthood),
  degreesOfChildhood: safePositiveNumeric(viewSettings.degreesOfChildhood),
});

const toSettableDegrees = (degrees: number): number =>
  Number.isFinite(degrees) ? degrees : INFINITE_DEGREES;

/**
 * The inverse of {@link readDependencyMapViewSettings}: the values you would
 * write back to reproduce the current state. `Infinity` has no JSON
 * representation, so anything reading settings in order to set them again -
 * the AI command, for one - needs this form rather than the in-memory one.
 * A fully expanded tree is settable as its maximum depth.
 */
export const toSettableDependencyMapViewSettings = (
  viewSettings: DependencyMapViewSettings,
  maxTreeDepth: number
): DependencyMapViewSettings => ({
  ...viewSettings,
  treeDepth: Number.isFinite(viewSettings.treeDepth)
    ? viewSettings.treeDepth
    : maxTreeDepth,
  degreesOfParenthood: toSettableDegrees(viewSettings.degreesOfParenthood),
  degreesOfChildhood: toSettableDegrees(viewSettings.degreesOfChildhood),
});
